(() => {
  const IMG_CACHE = new Map();
  const GALLERY_ORIG = new WeakMap();

  // Stock map is populated from the JSON embedded in Liquid
  let STOCK_MAP = null;
  let SHELL_IMAGES_MAP = null;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const parsePackSize = (value) => {
    if (!value) return 0;
    let v = String(value).trim();
    if (!v) return 0;

    // Common label formats: "3 Pack", "3-Pack", "3", "3x".
    v = v.split(' ')[0];
    if (v.includes('-')) v = v.split('-')[0];
    if (v.toLowerCase().endsWith('x')) v = v.slice(0, -1);
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const getAllSlots = (root) => $$('[data-bundle-slot]', root);
  const getActiveSlots = (root) => getAllSlots(root).filter((el) => !el.hasAttribute('hidden') && !el.classList.contains('bundle-slot--hidden'));

  const clearSlotSelection = (slotEl) => {
    $$('[data-bundle-swatch].is-selected', slotEl).forEach((btn) => {
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
    });
    const input = $('input[type="hidden"][data-bundle-selected-handle]', slotEl);
    if (input) input.value = '';
  };

  const clearAllSelections = (root) => {
    getAllSlots(root).forEach(clearSlotSelection);
    updateAllSlotLabels(root);
  };

  function updateSlotLabel(slot) {
    const labelEl = $("[data-bundle-slot-label]", slot);
    if (!labelEl) return;

    const selected = $("[data-bundle-swatch].is-selected", slot);
    const sr = selected ? $(".sr-only", selected) : null;
    const selectedLabel = ((selected && selected.getAttribute("aria-label")) || (sr ? sr.textContent : "") || "").trim();

    const fallback = labelEl.getAttribute("data-default-label") || labelEl.textContent || "";
    /* Hybrid shells: each picker is one pack, so keep the pack number visible.
       The native picker is Pack 1, rendered slots start at Pack 2. */
    if (slot.closest('[data-bundle-hybrid]')) {
      const n = (parseInt(slot.getAttribute('data-bundle-slot') || '0', 10) || 0) + 1;
      labelEl.textContent = selectedLabel ? ('Pack ' + n + ': ' + selectedLabel) : fallback;
      return;
    }
    labelEl.textContent = selectedLabel || fallback;
  }

  function updateAllSlotLabels(root) {
    $$("[data-bundle-slot]", root).forEach(updateSlotLabel);
  }

  function getRoot() {
    return $('[data-bundle-color-pickers]');
  }

  function getFormId(root) {
    return root?.getAttribute('data-form-id') || null;
  }

  /**
   * Get the bundle mode: 'predefined' or 'custom'
   */
  function getMode(root) {
    return root?.getAttribute('data-bundle-mode') || 'custom';
  }

  /**
   * Handleize a string to match Shopify's Liquid `handleize` filter.
   * "Charcoal Grey" → "charcoal-grey"
   * "Black/White/Charcoal Grey/Light Blue" → "black-white-charcoal-grey-light-blue"
   */
  function handleize(str) {
    return String(str)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')     // any non-alphanumeric run → single hyphen
      .replace(/^-|-$/g, '');           // trim leading/trailing hyphens
  }

  /**
   * Resolve a color option value to an array of base-product color handles.
   * - "Black/White/Charcoal Grey/Light Blue" pack 4 → ["black", "white", "charcoal-grey", "light-blue"]
   * - "Black/White/Charcoal Grey/Light Blue" pack 8 → cycles ×2
   * - "Black" pack 4 → ["black", "black", "black", "black"]
   * 
   * Single colors repeat to fill pack size. Slash combos cycle to fill pack size.
   */
  function resolveColorValue(colorValue, packSize) {
    if (!colorValue) return [];

    let parts;
    if (colorValue.includes('/')) {
      parts = colorValue.split('/').map(part => handleize(part));
    } else {
      parts = [handleize(colorValue)];
    }

    // Cycle parts to fill pack size
    const count = packSize || parts.length;
    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(parts[i % parts.length]);
    }
    return result;
  }

  /**
   * Get unique handles from an array (preserving order).
   */
  function uniqueHandles(handles) {
    return [...new Set(handles)];
  }

  /**
   * Render the read-only swatch display for predefined mode.
   * Shows circle swatches with colors from the stock map.
   */
  function renderPredefinedDisplay(root, handles, colorValue, soldOutColors = []) {
    const labelEl = $('[data-bundle-predefined-label]', root);
    const swatchContainer = $('[data-bundle-predefined-swatches]', root);
    if (!swatchContainer) return;

    // Update label with the color value (e.g. "Black/White/Grey")
    if (labelEl) {
      labelEl.textContent = colorValue || '';
    }

    // Clear existing swatches
    swatchContainer.innerHTML = '';

    const map = loadStockMap();

    // Show only unique colors (a 8-pack of Black still shows 1 swatch)
    const unique = uniqueHandles(handles);

    unique.forEach(handle => {
      const colorData = map[handle];
      const dot = document.createElement('span');
      dot.className = 'bundle-predefined-display__swatch';

      const inner = document.createElement('span');
      inner.className = 'bundle-predefined-display__swatch-inner';
      if (colorData?.swatch) {
        inner.setAttribute('style', colorData.swatch);
      } else {
        inner.style.backgroundColor = handle;
      }
      dot.appendChild(inner);

      const label = colorData?.label || handle;
      dot.setAttribute('title', label);

      // Mark sold out
      if (soldOutColors.some(c => c.toLowerCase() === label.toLowerCase() || c === handle)) {
        dot.classList.add('is-soldout');
      }

      swatchContainer.appendChild(dot);
    });
  }

  /**
   * Apply correct sold-out state to the theme's variant picker in predefined mode.
   * Uses the base product's stock map (not the shell product's availability).
   *
   * - Packs: never marked sold out
   * - Colors: sold out if ANY constituent color is unavailable in current size
   * - Sizes: sold out if ANY color in current selection is unavailable in that size
   */
  function applyBundleSoldOut(root, formId) {
    const colorPos = root.getAttribute('data-shell-color-position') || '2';
    const sizePos = root.getAttribute('data-shell-size-position') || '3';
    const packSize = getPackSize(root);
    const currentSize = getSelectedSize(formId, root);
    const currentColor = getSelectedCombo(formId, root) || '';

    // --- COLOR OPTIONS: mark sold out if any constituent OOS in current size ---
    if (currentSize) {
      const colorInputs = $$(`input[name="option${colorPos}"]`);
      for (const input of colorInputs) {
        const value = input.value || '';
        if (!value) continue;

        const handles = resolveColorValue(value, packSize);
        const unique = uniqueHandles(handles);

        const anySoldOut = unique.some(handle => {
          const stockInfo = getStockInfo(handle, currentSize);
          return !stockInfo || !stockInfo.available;
        });

        const label = input.nextElementSibling;
        if (label) {
          if (anySoldOut) {
            label.classList.add('is-disabled');
          } else {
            label.classList.remove('is-disabled');
          }
        }
      }
    }

    // --- SIZE OPTIONS: mark sold out if any color in current selection is OOS in that size ---
    if (currentColor) {
      const handles = resolveColorValue(currentColor, packSize);
      const unique = uniqueHandles(handles);

      const sizeInputs = $$(`input[name="option${sizePos}"]`);
      for (const input of sizeInputs) {
        const sizeValue = input.value || '';
        if (!sizeValue) continue;

        const anySoldOut = unique.some(handle => {
          const stockInfo = getStockInfo(handle, sizeValue);
          return !stockInfo || !stockInfo.available;
        });

        const label = input.nextElementSibling;
        if (label) {
          if (anySoldOut) {
            label.classList.add('is-disabled');
          } else {
            label.classList.remove('is-disabled');
          }
        }
      }
    }

    // --- PACK OPTIONS: never mark sold out ---
    $$('input[name="option1"]').forEach(input => {
      const label = input.nextElementSibling;
      if (label) label.classList.remove('is-disabled');
    });
  }

  /**
   * Get the shell product's size option position.
   * In predefined mode this may be option3, in custom mode it's option2.
   */
  function getSizeOptionPosition(root) {
    if (getMode(root) === 'predefined') {
      return parseInt(root.getAttribute('data-shell-size-position') || '3', 10);
    }
    return 2;
  }

  /**
   * Get the shell product's color combo option position (predefined mode only).
   */
  function getColorComboOptionPosition(root) {
    return parseInt(root.getAttribute('data-shell-color-position') || '2', 10);
  }

  /**
   * Get the selected value for an option by position (1-indexed).
   * Handles radio buttons, hidden inputs, and selects.
   * Also checks for theme's x-listbox / popover hidden inputs.
   */
  function getSelectedOption(formId, optionPosition) {
    if (!formId) return null;
    
    const optionName = `option${optionPosition}`;
    
    // Strategy 1: Check radio buttons with form attribute
    const radio = $(`input[name="${optionName}"][form="${formId}"]:checked`);
    if (radio?.value) return radio.value;

    // Strategy 2: Check select elements with form attribute
    const select = $(`select[name="${optionName}"][form="${formId}"]`);
    if (select?.value) return select.value;

    // Strategy 3: Check hidden inputs with form attribute (used by dropdowns/popovers)
    const hidden = $(`input[name="${optionName}"][type="hidden"][form="${formId}"]`);
    if (hidden?.value) return hidden.value;

    // Strategy 4: Look inside the actual form element
    const formEl = document.getElementById(formId);
    if (formEl) {
      const radioInForm = $(`input[name="${optionName}"]:checked`, formEl);
      if (radioInForm?.value) return radioInForm.value;

      const selectInForm = $(`select[name="${optionName}"]`, formEl);
      if (selectInForm?.value) return selectInForm.value;

      const hiddenInForm = $(`input[name="${optionName}"][type="hidden"]`, formEl);
      if (hiddenInForm?.value) return hiddenInForm.value;
    }

    // Strategy 5: Check variant-picker component for this form
    const variantPicker = $(`variant-picker[form="${formId}"]`);
    if (variantPicker) {
      const radioInPicker = $(`input[name="${optionName}"]:checked`, variantPicker);
      if (radioInPicker?.value) return radioInPicker.value;

      const hiddenInPicker = $(`input[name="${optionName}"][type="hidden"]`, variantPicker);
      if (hiddenInPicker?.value) return hiddenInPicker.value;
    }

    // Strategy 6: Global fallback - find any input with this name that's checked/has value
    const anyRadio = $(`input[name="${optionName}"]:checked`);
    if (anyRadio?.value) return anyRadio.value;

    const anyHidden = $(`input[name="${optionName}"][type="hidden"]`);
    if (anyHidden?.value) return anyHidden.value;

    return null;
  }

  // Pack Size is option1 (first option on product)
  function getSelectedPackSize(formId) {
    const value = getSelectedOption(formId, 1);
    return parsePackSize(value);
  }

  // Size position varies: option2 in custom mode, option3 in predefined mode
  function getSelectedSize(formId, root) {
    const pos = root ? getSizeOptionPosition(root) : 2;
    return getSelectedOption(formId, pos);
  }

  /**
   * Get the selected color combo value (predefined mode only).
   */
  function getSelectedCombo(formId, root) {
    if (getMode(root) !== 'predefined') return null;
    const pos = getColorComboOptionPosition(root);
    return getSelectedOption(formId, pos);
  }

  /**
   * Load the stock map from the embedded JSON in Liquid.
   * This is called once on init and provides instant stock lookups.
   */
  function loadStockMap() {
    if (STOCK_MAP) return STOCK_MAP;

    const script = $('script[data-bundle-stock-map]');
    if (!script) {
      console.warn('[bundle-builder] No stock map found. Falling back to always-available.');
      STOCK_MAP = {};
      return STOCK_MAP;
    }

    try {
      STOCK_MAP = JSON.parse(script.textContent);
    } catch (e) {
      console.error('[bundle-builder] Failed to parse stock map:', e);
      STOCK_MAP = {};
    }

    return STOCK_MAP;
  }

  /**
   * Normalize a size value to its common abbreviation.
   * "Small" → "S", "Medium" → "M", "Large" → "L", etc.
   * Also handles reverse: "S" → try "Small", etc.
   */
  const SIZE_ALIASES = {
    'small': ['s', 'sm'],
    'medium': ['m', 'med'],
    'large': ['l', 'lg'],
    'x-large': ['xl', 'x-large', 'extra large', 'extra-large'],
    'extra small': ['xs', 'x-small', 'extra-small'],
    'extra extra small': ['xxs', '2xs'],
    'extra extra large': ['xxl', '2xl', '2x'],
    'extra extra extra large': ['xxxl', '3xl', '3x'],
    // Abbreviation → full
    's': ['small', 'sm'],
    'm': ['medium', 'med'],
    'l': ['large', 'lg'],
    'xs': ['extra small', 'x-small', 'extra-small'],
    'sm': ['small', 's'],
    'xl': ['x-large', 'extra large', 'extra-large'],
    'xxs': ['extra extra small', '2xs'],
    'xxl': ['extra extra large', '2xl', '2x'],
    '2xl': ['xxl', 'extra extra large', '2x'],
    '3xl': ['xxxl', 'extra extra extra large', '3x'],
  };

  function getSizeAliases(size) {
    const norm = String(size).trim().toLowerCase();
    const aliases = SIZE_ALIASES[norm] || [];
    // Always include the original (case-insensitive)
    return [norm, ...aliases];
  }

  /**
   * Get stock info for a handle + size from the embedded map.
   * Returns { available: boolean, variantId: number } or null if not found.
   */
  function getStockInfo(handle, size) {
    const map = loadStockMap();
    let product = map[handle];

    // Fuzzy handle match: try re-handleizing and partial matching
    if (!product) {
      const reHandled = handleize(handle);
      if (reHandled !== handle) product = map[reHandled];
    }
    if (!product) {
      // Try matching stock map keys that contain the handle or vice versa
      for (const [key, val] of Object.entries(map)) {
        if (key === handle || handleize(key) === handleize(handle)) {
          product = val;
          break;
        }
      }
    }

    if (!product || !product.sizes) return null;

    // Try exact match first
    if (product.sizes[size]) {
      return product.sizes[size];
    }

    // Try case-insensitive match
    const sizeNorm = String(size).trim().toLowerCase();
    for (const [key, val] of Object.entries(product.sizes)) {
      if (String(key).trim().toLowerCase() === sizeNorm) {
        return val;
      }
    }

    // Try size aliases ("Small" ↔ "S", "Medium" ↔ "M", etc.)
    const aliases = getSizeAliases(size);
    for (const alias of aliases) {
      for (const [key, val] of Object.entries(product.sizes)) {
        if (String(key).trim().toLowerCase() === alias) {
          return val;
        }
      }
    }

    return null;
  }

  /**
   * Get the product image URL from the embedded map.
   */
  function getProductImage(handle) {
    const map = loadStockMap();
    let product = map[handle];
    if (!product) product = map[handleize(handle)];
    if (!product) {
      for (const [key, val] of Object.entries(map)) {
        if (handleize(key) === handleize(handle)) { product = val; break; }
      }
    }
    return product?.image || null;
  }

  /**
   * Get ALL images for a color handle from the stock map.
   * Returns array of image URLs, or falls back to single image in an array.
   */
  function getProductImages(handle) {
    const map = loadStockMap();
    let product = map[handle];
    if (!product) product = map[handleize(handle)];
    if (!product) {
      for (const [key, val] of Object.entries(map)) {
        if (handleize(key) === handleize(handle)) { product = val; break; }
      }
    }
    if (product?.images && product.images.length > 0) {
      return product.images;
    }
    return product?.image ? [product.image] : [];
  }

  /**
   * Get model sizing HTML for a color handle from the stock map.
   * Returns HTML string from base product's model.sizing_1 or model.sizing_2 metafield,
   * as extracted from the first image's #model_A / #model_B alt tag.
   */
  function getModelSizing(handle) {
    const map = loadStockMap();
    let product = map[handle];
    if (!product) product = map[handleize(handle)];
    if (!product) {
      for (const [key, val] of Object.entries(map)) {
        if (handleize(key) === handleize(handle)) { product = val; break; }
      }
    }
    return product?.modelSizing || '';
  }

  /**
   * Update the model sizing overlay on the first visible gallery slot.
   * Shows the overlay only on the very first image, using data from the base product.
   */
  function updateModelSizing(gallery, firstHandle) {
    // Remove any existing model sizing overlays
    $$('.bundle-gallery__model-sizing', gallery).forEach(el => el.remove());

    if (!firstHandle) return;

    const html = getModelSizing(firstHandle);
    if (!html) return;

    // Find the first visible slot
    const firstSlot = $('[data-bundle-gallery-slot]:not([hidden])', gallery);
    if (!firstSlot) return;

    const overlay = document.createElement('div');
    overlay.className = 'bundle-gallery__model-sizing';
    overlay.innerHTML = html;
    firstSlot.appendChild(overlay);
  }

  /**
   * Load shell product images map from embedded JSON.
   * Maps color option values (handleized) to arrays of shell product image URLs.
   */
  function loadShellImagesMap() {
    if (SHELL_IMAGES_MAP) return SHELL_IMAGES_MAP;
    const script = $('script[data-bundle-shell-images]');
    if (!script) {
      SHELL_IMAGES_MAP = {};
      return SHELL_IMAGES_MAP;
    }
    try {
      SHELL_IMAGES_MAP = JSON.parse(script.textContent);
    } catch (e) {
      console.warn('[bundle-builder] Failed to parse shell images map:', e);
      SHELL_IMAGES_MAP = {};
    }
    return SHELL_IMAGES_MAP;
  }

  /**
   * Get shell product images for a color option value.
   * Tries exact handle match, then fuzzy.
   * @param {string} colorValue - The full option value, e.g. "Black" or "Black/White/Charcoal Grey/Light Blue"
   * @returns {string[]} Array of image URLs
   */
  function getShellImages(colorValue) {
    const map = loadShellImagesMap();
    const handle = handleize(colorValue);
    if (map[handle]) return map[handle];
    // Fuzzy match
    for (const [key, val] of Object.entries(map)) {
      if (handleize(key) === handle) return val;
    }
    return [];
  }

  /**
   * Preload an image URL and return the fully-loaded Image object.
   * Uses img.decode() when available for smoother rendering.
   */
  function preloadImage(url) {
    if (!url) return Promise.resolve(null);
    if (IMG_CACHE.has(url)) return IMG_CACHE.get(url);

    const p = new Promise((resolve) => {
      const img = new Image();

      img.onload = async () => {
        // Use decode() if available for smoother swap
        if (typeof img.decode === 'function') {
          try {
            await img.decode();
          } catch (e) {
            // decode() can fail on some images, continue anyway
          }
        }
        resolve(img);
      };

      img.onerror = () => resolve(null);
      img.src = url;
    });

    IMG_CACHE.set(url, p);
    return p;
  }

  function addWidthParam(url, width) {
    if (!url) return url;
    const w = Number(width || 1200);
    if (!w) return url;
    // If URL already has width param, replace it
    if (url.includes('width=')) {
      return url.replace(/width=\d+/, `width=${w}`);
    }
    return url.includes('?') ? `${url}&width=${w}` : `${url}?width=${w}`;
  }

  function getGallery(root) {
    const section = root.closest('.shopify-section') || document;
    
    // First, check for the new bundle-gallery component
    const bundleGallery = $('[data-bundle-gallery]', section) || $('[data-bundle-gallery]');
    if (bundleGallery) return bundleGallery;
    
    // Fall back to the standard product-gallery
    return $('product-gallery', section) || $('product-gallery');
  }

  /**
   * Check if we're using the new bundle-gallery component
   */
  function isBundleGallery(gallery) {
    return gallery && gallery.hasAttribute('data-bundle-gallery');
  }

  function snapshotGallery(gallery) {
    if (!gallery || GALLERY_ORIG.has(gallery)) return;

    const mediaEls = $$('.product-gallery__media', gallery);
    const thumbEls = $$('.product-gallery__thumbnail', gallery);

    const mediaImgs = mediaEls.map(el => $('img', el));
    const thumbImgs = thumbEls.map(el => $('img', el));

    const mediaState = mediaEls.map((el, i) => {
      const img = mediaImgs[i];
      return {
        hidden: el.hidden,
        innerHTML: el.innerHTML, // Capture original HTML for placeholder restoration
        src: img ? img.getAttribute('src') : null,
        srcset: img ? img.getAttribute('srcset') : null,
        sizes: img ? img.getAttribute('sizes') : null,
      };
    });

    const thumbState = thumbEls.map((el, i) => {
      const img = thumbImgs[i];
      return {
        hidden: el.hidden,
        ariaCurrent: el.getAttribute('aria-current'),
        src: img ? img.getAttribute('src') : null,
        srcset: img ? img.getAttribute('srcset') : null,
        sizes: img ? img.getAttribute('sizes') : null,
      };
    });

    // Find the containers for media and thumbnails
    const carousel = $('.product-gallery__carousel, .scroll-carousel, scroll-carousel', gallery);
    const thumbContainer = $('.product-gallery__thumbnail-list, product-gallery-navigation, .product-gallery__thumbnail-scroller', gallery);

    GALLERY_ORIG.set(gallery, {
      mediaEls,
      thumbEls,
      mediaImgs,
      thumbImgs,
      mediaState,
      thumbState,
      carousel,
      thumbContainer,
      // Track cloned elements so we can remove them on restore
      clonedMediaEls: [],
      clonedThumbEls: [],
    });
  }

  function restoreGallery(gallery) {
    const orig = gallery ? GALLERY_ORIG.get(gallery) : null;
    if (!orig) return;

    // Remove any cloned elements first
    orig.clonedMediaEls.forEach(el => el.remove());
    orig.clonedThumbEls.forEach(el => el.remove());
    orig.clonedMediaEls = [];
    orig.clonedThumbEls = [];

    // Restore original media elements
    orig.mediaEls.forEach((el, i) => {
      const st = orig.mediaState[i];
      if (!st) return;
      
      el.hidden = !!st.hidden;
      el.removeAttribute('data-bundle-placeholder');
      
      // Restore original innerHTML if it was replaced with placeholder
      if (st.innerHTML && el.querySelector('.bundle-gallery-placeholder')) {
        el.innerHTML = st.innerHTML;
      }
      
      // Re-query for img since innerHTML might have been restored
      const img = $('img', el);
      if (img) {
        if (st.src != null) img.setAttribute('src', st.src);
        if (st.srcset != null) img.setAttribute('srcset', st.srcset);
        else img.removeAttribute('srcset');
        if (st.sizes != null) img.setAttribute('sizes', st.sizes);
        else img.removeAttribute('sizes');
        img.classList.add('is-loaded');
      }
    });

    // Update the mediaImgs array since we may have restored innerHTML
    orig.mediaImgs = orig.mediaEls.map(el => $('img', el));

    // Restore original thumbnail elements
    orig.thumbEls.forEach((el, i) => {
      el.hidden = !!orig.thumbState[i]?.hidden;
      const st = orig.thumbState[i];
      if (st) {
        if (st.ariaCurrent == null) el.removeAttribute('aria-current');
        else el.setAttribute('aria-current', st.ariaCurrent);
      }
      const img = orig.thumbImgs[i];
      if (!img || !st) return;
      if (st.src != null) img.setAttribute('src', st.src);
      if (st.srcset != null) img.setAttribute('srcset', st.srcset);
      else img.removeAttribute('srcset');
      if (st.sizes != null) img.setAttribute('sizes', st.sizes);
      else img.removeAttribute('sizes');
      img.classList.add('is-loaded');
    });
  }

  /**
   * Clone a media element for additional gallery slots
   */
  function cloneMediaElement(templateEl, index) {
    const clone = templateEl.cloneNode(true);
    clone.hidden = false;
    clone.classList.remove('is-initial', 'active');
    clone.setAttribute('data-bundle-cloned', 'true');
    clone.setAttribute('aria-label', `Item ${index + 1}`);
    
    // Clear any media IDs to avoid conflicts
    clone.removeAttribute('data-media-id');
    
    // Get the image and clear its src (will be set later)
    const img = $('img', clone);
    if (img) {
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.classList.remove('is-loaded');
    }
    
    return clone;
  }

  /**
   * Clone a thumbnail element for additional gallery slots
   */
  function cloneThumbnailElement(templateEl, index) {
    const clone = templateEl.cloneNode(true);
    clone.hidden = false;
    clone.setAttribute('data-bundle-cloned', 'true');
    clone.setAttribute('aria-current', 'false');
    clone.setAttribute('aria-label', `Go to item ${index + 1}`);
    
    // Clear any media IDs to avoid conflicts
    clone.removeAttribute('data-media-id');
    clone.removeAttribute('data-media-position');
    
    // Get the image and clear its src (will be set later)
    const img = $('img', clone);
    if (img) {
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.classList.remove('is-loaded');
    }
    
    return clone;
  }

  /**
   * Swap an image src only after it's fully loaded + decoded.
   * This prevents the flash/pop-in by ensuring the browser has the
   * image ready to paint before we update the DOM.
   */
  async function swapImageSmooth(imgEl, newUrl) {
    if (!imgEl || !newUrl) return;

    // Remove loaded class to prepare for potential transition
    imgEl.classList.remove('is-loaded');

    // Preload and decode the new image
    const preloaded = await preloadImage(newUrl);

    // Now swap - browser should paint immediately since image is cached + decoded
    imgEl.setAttribute('src', newUrl);
    imgEl.removeAttribute('srcset');
    imgEl.removeAttribute('sizes');

    // If preload succeeded and decode is available, decode the actual element
    if (preloaded && typeof imgEl.decode === 'function') {
      try {
        await imgEl.decode();
      } catch (e) {
        // Ignore decode errors
      }
    }

    // Add loaded class for CSS transition
    imgEl.classList.add('is-loaded');
  }

  /**
   * Get the ordinal suffix for a number (1st, 2nd, 3rd, etc.)
   */
  function getOrdinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  async function updateBundleGallery(root, clickedSlotIndex = null, options = {}) {
    /* Hybrid shells: the native picker owns the gallery (shell-gallery-sync).
       Slot picks are additional colours and must not move or filter media. */
    if (root && root.hasAttribute('data-bundle-hybrid')) return;
    const gallery = getGallery(root);
    if (!gallery) return;

    // Use the new simple bundle-gallery component
    if (isBundleGallery(gallery)) {
      await updateBundleGallerySimple(root, gallery, clickedSlotIndex, options);
      return;
    }

    // Fall back to old product-gallery logic (for backwards compatibility)
    await updateProductGallery(root, gallery);
  }

  /**
   * Update the new bundle-gallery component (simple CSS grid with pre-rendered slots)
   * 
   * Behavior:
   * - No selections: Show shell product images (initial state from Liquid)
   * - Some selections: Show selected color images in order, then ONE placeholder for next slot
   * - All selections: Show all color images, no placeholder
   * 
   * @param {number|null} clickedSlotIndex - The slot that was just clicked (1-based), for scroll targeting
   * @param {object} options - { skipScroll: boolean }
   */
  async function updateBundleGallerySimple(root, gallery, clickedSlotIndex = null, options = {}) {
    const { skipScroll = false } = options;
    
    const isMobile = window.innerWidth < 1000;
    
    // ── Scroll-anchor fix (mobile) ──────────────────────────────────
    // On mobile, the gallery sits ABOVE product-info in the CSS grid.
    // When gallery slots hide/show, the grid row height changes and
    // pushes product-info up or down — visible as a "page jump."
    //
    // Fix: record the viewport-relative top of product-info before any
    // DOM changes, then after changes, adjust window.scrollY so that
    // product-info stays in the same visual position.
    let scrollAnchor = null;
    if (isMobile) {
      const anchorEl = $('[data-bundle-color-pickers]') || $('.product-info');
      if (anchorEl) {
        scrollAnchor = {
          el: anchorEl,
          viewportTop: anchorEl.getBoundingClientRect().top,
        };
      }
    }
    
    // Lock gallery height as secondary guard against mid-frame flicker
    const galleryRect = gallery.getBoundingClientRect();
    gallery.style.minHeight = galleryRect.height + 'px';

    // Get the required number of slots (from pack size)
    const requiredSlots = parseInt(root.dataset.requiredSlots || '0', 10) || getActiveSlots(root).length;
    
    // Get selections in slot order (includes nulls for empty slots)
    const slotHandles = getSelections(root);
    
    // Count how many are actually selected
    const selectedCount = slotHandles.filter(Boolean).length;
    
    // Get all slot elements in the gallery
    const slotEls = $$('[data-bundle-gallery-slot]', gallery);
    
    console.log('[bundle-builder] Gallery update:', { 
      requiredSlots, 
      slotHandles, 
      selectedCount,
      slotCount: slotEls.length 
    });

    // No selections: restore shell product images (initial state)
    if (!selectedCount) {
      restoreBundleGallery(gallery, slotEls);
      // Clear model sizing when no colors selected
      updateModelSizing(gallery, null);
      releaseGalleryHeight(gallery, scrollAnchor);
      return;
    }

    // Build display list: selected images in order, then ONE placeholder for next empty slot
    const displayItems = [];
    
    const mode = getMode(root);
    
    if (mode === 'predefined') {
      const unique = uniqueHandles(slotHandles.filter(Boolean));
      const formId = getFormId(root);
      const colorValue = getSelectedCombo(formId, root) || '';
      const seenUrls = new Set();

      // Shell product images first (flatlays, pack shots)
      const shellImgs = getShellImages(colorValue);
      for (let i = 0; i < shellImgs.length; i++) {
        if (!seenUrls.has(shellImgs[i])) {
          seenUrls.add(shellImgs[i]);
          displayItems.push({ type: 'image', handle: unique[0] || '', imageUrl: shellImgs[i], slotIndex: displayItems.length + 1 });
        }
      }

      // Then base product images
      if (unique.length === 1) {
        // Single color (e.g. "Black" 4-pack): show ALL images for that color
        const allImages = getProductImages(unique[0]);
        for (let i = 0; i < allImages.length; i++) {
          if (!seenUrls.has(allImages[i])) {
            seenUrls.add(allImages[i]);
            displayItems.push({ type: 'image', handle: unique[0], imageUrl: allImages[i], slotIndex: displayItems.length + 1 });
          }
        }
      } else {
        // Multi-color: one image per color from base product
        for (let i = 0; i < unique.length; i++) {
          const img = getProductImage(unique[i]);
          if (img && !seenUrls.has(img)) {
            seenUrls.add(img);
            displayItems.push({ type: 'image', handle: unique[i], imageUrl: img, slotIndex: displayItems.length + 1 });
          }
        }
      }
    } else if (requiredSlots === 1) {
      // Single-slot custom mode (e.g. Distressed Tank 2-Pack, units_per_slot=2 → 1 slot):
      // swapping the whole gallery for one image feels broken. Show the full set of images
      // for the selected color instead, like predefined mode does when only one color is picked.
      const handle = slotHandles.find(Boolean);
      if (handle) {
        const seenUrls = new Set();
        const allImages = getProductImages(handle);
        for (let i = 0; i < allImages.length; i++) {
          if (!seenUrls.has(allImages[i])) {
            seenUrls.add(allImages[i]);
            displayItems.push({ type: 'image', handle, imageUrl: allImages[i], slotIndex: displayItems.length + 1 });
          }
        }
        // If the base product happens to have no color-tagged images, fall back to a single
        // featured image so we don't end up with zero items.
        if (!displayItems.length) {
          displayItems.push({ type: 'image', handle, slotIndex: 1 });
        }
      }
    } else {
      // Custom mode (multi-slot): one image per slot selection.
      for (let i = 0; i < slotHandles.length; i++) {
        const handle = slotHandles[i];
        if (handle) {
          displayItems.push({ type: 'image', handle, slotIndex: displayItems.length + 1 });
        }
      }

      // Add ONE placeholder for the next slot (if not all filled)
      if (selectedCount < requiredSlots) {
        displayItems.push({ type: 'placeholder', slotIndex: displayItems.length + 1 });
      }
    }

    // Ensure we have enough slot elements
    while (slotEls.length < displayItems.length) {
      const newSlot = createGallerySlot(gallery, slotEls.length + 1);
      gallery.appendChild(newSlot);
      slotEls.push(newSlot);
    }

    // Preload images first
    const preloadTasks = displayItems
      .filter(item => item.type === 'image')
      .map(item => {
        const imageUrl = item.imageUrl || getProductImage(item.handle);
        return imageUrl ? preloadImage(addWidthParam(imageUrl, 800)) : Promise.resolve();
      });
    
    await Promise.allSettled(preloadTasks);

    // Track slots for scrolling
    let lastImageSlot = null;
    let scrollTargetSlot = null;

    // Update each slot
    for (let i = 0; i < slotEls.length; i++) {
      const slotEl = slotEls[i];
      const item = displayItems[i];
      
      if (!item) {
        // Hide extra slots
        slotEl.hidden = true;
        continue;
      }
      
      slotEl.hidden = false;
      
      if (item.type === 'image') {
        const imageUrl = item.imageUrl || getProductImage(item.handle);
        if (imageUrl) {
          await showSlotImage(slotEl, addWidthParam(imageUrl, 800), item.slotIndex);
          lastImageSlot = slotEl;
          
          // Track if this is the slot that was just clicked
          if (clickedSlotIndex && item.slotIndex === clickedSlotIndex) {
            scrollTargetSlot = slotEl;
          }
        }
      } else if (item.type === 'placeholder') {
        showSlotPlaceholder(slotEl, item.slotIndex);
      }
    }

    // Update model sizing overlay on the first image (from base product data)
    const firstImageItem = displayItems.find(item => item.type === 'image' && item.handle);
    updateModelSizing(gallery, firstImageItem?.handle || null);

    // Release height lock and restore scroll position
    releaseGalleryHeight(gallery, scrollAnchor);

    // On desktop only, scroll to the image that was just changed (custom/mix-and-match only).
    // Skip in single-slot mode — there's no meaningful "just-picked slot" to anchor to, and
    // scrolling the whole gallery feels like a glitch on a color change.
    if (!skipScroll && mode !== 'predefined' && requiredSlots > 1) {
      const isDesktop = window.matchMedia('(min-width: 1000px)').matches;
      const targetSlot = scrollTargetSlot || lastImageSlot;
      if (isDesktop && targetSlot) {
        requestAnimationFrame(() => {
          targetSlot.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }
  }

  /**
   * Release the gallery minHeight lock and correct scroll position on mobile
   * so that the element the user is interacting with doesn't visually shift.
   */
  function releaseGalleryHeight(gallery, scrollAnchor) {
    // Release minHeight so the grid can settle to its natural size
    gallery.style.minHeight = '';

    // Mark gallery as ready (removes predefined loading hide)
    gallery.classList.add('is-ready');

    // Correct scroll drift on mobile
    if (scrollAnchor && scrollAnchor.el) {
      // Force layout so getBoundingClientRect reflects the new height
      const viewportTopAfter = scrollAnchor.el.getBoundingClientRect().top;
      const drift = viewportTopAfter - scrollAnchor.viewportTop;

      if (Math.abs(drift) > 1) {
        // Instant scroll adjustment — no smooth behavior, user shouldn't perceive it
        window.scrollBy({ top: drift, left: 0, behavior: 'instant' });
      }
    }
  }

  /**
   * Restore the bundle gallery to show shell product images (initial state)
   * Shows ALL shell product images - no limiting by pack size
   */
  function restoreBundleGallery(gallery, slotEls) {
    slotEls.forEach((slotEl, i) => {
      slotEl.removeAttribute('data-bundle-placeholder');
      
      // Hide placeholder
      const placeholder = $('.bundle-gallery__placeholder', slotEl);
      if (placeholder) {
        placeholder.style.display = 'none';
      }
      
      // Restore original image if it has one
      const img = $('img', slotEl);
      if (img) {
        const originalSrc = img.getAttribute('data-original-src');
        if (originalSrc) {
          img.src = originalSrc;
          img.style.display = '';
          img.classList.add('is-loaded');
          slotEl.hidden = false;
        } else {
          // No original image - hide this slot entirely
          slotEl.hidden = true;
        }
      } else {
        slotEl.hidden = true;
      }
    });
  }

  /**
   * Create a new gallery slot element
   */
  function createGallerySlot(gallery, slotIndex) {
    const slot = document.createElement('div');
    slot.className = 'bundle-gallery__media';
    slot.setAttribute('data-bundle-gallery-slot', slotIndex);
    slot.setAttribute('role', 'group');
    slot.setAttribute('aria-label', `Slot ${slotIndex}`);
    
    // Add placeholder content
    showSlotPlaceholder(slot, slotIndex);
    
    // Add hidden img for later use
    const img = document.createElement('img');
    img.src = '';
    img.alt = '';
    img.loading = 'lazy';
    img.style.display = 'none';
    img.style.position = 'absolute';
    img.style.inset = '0';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    slot.appendChild(img);
    
    return slot;
  }

  /**
   * Show placeholder in a gallery slot
   */
  function showSlotPlaceholder(slotEl, slotIndex) {
    slotEl.setAttribute('data-bundle-placeholder', 'true');
    
    // Hide image if present
    const img = $('img', slotEl);
    if (img) {
      img.style.display = 'none';
      img.classList.remove('is-loaded');
    }
    
    // Show placeholder content
    let placeholder = $('.bundle-gallery__placeholder', slotEl);
    if (!placeholder) {
      // Create placeholder
      placeholder = document.createElement('div');
      placeholder.className = 'bundle-gallery__placeholder';
      placeholder.innerHTML = `
        <div class="bundle-gallery__placeholder-icon">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </div>
        <span class="bundle-gallery__placeholder-text">${slotIndex}</span>
      `;
      slotEl.insertBefore(placeholder, slotEl.firstChild);
    } else {
      placeholder.style.display = '';
      // Update text
      const textEl = $('.bundle-gallery__placeholder-text', placeholder);
      if (textEl) {
        textEl.textContent = `${slotIndex}`;
      }
    }
  }

  /**
   * Show image in a gallery slot
   */
  async function showSlotImage(slotEl, imageUrl, slotIndex) {
    slotEl.removeAttribute('data-bundle-placeholder');
    
    // Hide placeholder
    const placeholder = $('.bundle-gallery__placeholder', slotEl);
    if (placeholder) {
      placeholder.style.display = 'none';
    }
    
    // Show and update image
    let img = $('img', slotEl);
    if (!img) {
      img = document.createElement('img');
      img.loading = 'lazy';
      slotEl.appendChild(img);
    }
    
    img.style.display = '';
    img.classList.remove('is-loaded');
    
    // Preload and swap
    await preloadImage(imageUrl);
    img.src = imageUrl;
    img.alt = `Bundle item ${slotIndex}`;
    
    if (typeof img.decode === 'function') {
      try {
        await img.decode();
      } catch (e) {}
    }
    
    img.classList.add('is-loaded');
  }

  /**
   * Legacy: Update the old product-gallery component
   */
  async function updateProductGallery(root, gallery) {
    snapshotGallery(gallery);

    const orig = GALLERY_ORIG.get(gallery);
    if (!orig) return;

    // Get the required number of slots (from pack size)
    const requiredSlots = parseInt(root.dataset.requiredSlots || '0', 10) || getActiveSlots(root).length;
    
    // Get selections in slot order (includes nulls for empty slots)
    const slotHandles = getSelections(root);
    
    // Count how many are actually selected
    const selectedCount = slotHandles.filter(Boolean).length;

    // No selections: show shell product gallery as-is
    if (!selectedCount) {
      restoreGallery(gallery);
      return;
    }

    const originalMediaCount = orig.mediaEls.length;
    const originalThumbCount = orig.thumbEls.length;

    // Remove old clones and placeholders first
    orig.clonedMediaEls.forEach(el => el.remove());
    orig.clonedThumbEls.forEach(el => el.remove());
    orig.clonedMediaEls = [];
    orig.clonedThumbEls = [];

    // Build display items: images first, then one placeholder
    const displayItems = [];
    
    for (let i = 0; i < slotHandles.length; i++) {
      const handle = slotHandles[i];
      if (handle) {
        displayItems.push({ type: 'image', handle, slotIndex: i + 1 });
      }
    }
    
    // Add one placeholder for the next empty slot
    if (selectedCount < requiredSlots) {
      for (let i = 0; i < requiredSlots; i++) {
        if (!slotHandles[i]) {
          displayItems.push({ type: 'placeholder', slotIndex: i + 1 });
          break;
        }
      }
    }

    const neededCount = displayItems.length;

    // Build arrays of all media/thumb elements we'll use (original + clones)
    let allMediaEls = [...orig.mediaEls];
    let allMediaImgs = [...orig.mediaImgs];
    let allThumbEls = [...orig.thumbEls];
    let allThumbImgs = [...orig.thumbImgs];

    // Clone additional media elements if needed
    if (neededCount > originalMediaCount && orig.carousel && orig.mediaEls[0]) {
      const template = orig.mediaEls[0];
      for (let i = originalMediaCount; i < neededCount; i++) {
        const clone = cloneMediaElement(template, i);
        orig.carousel.appendChild(clone);
        orig.clonedMediaEls.push(clone);
        allMediaEls.push(clone);
        allMediaImgs.push($('img', clone));
      }
    }

    // Clone additional thumbnail elements if needed
    if (neededCount > originalThumbCount && orig.thumbContainer && orig.thumbEls[0]) {
      const template = orig.thumbEls[0];
      for (let i = originalThumbCount; i < neededCount; i++) {
        const clone = cloneThumbnailElement(template, i);
        orig.thumbContainer.appendChild(clone);
        orig.clonedThumbEls.push(clone);
        allThumbEls.push(clone);
        allThumbImgs.push($('img', clone));
      }
    }

    const maxMedia = Math.min(neededCount, allMediaEls.length);

    // Hide all media elements first
    orig.mediaEls.forEach(el => { el.hidden = true; });
    orig.thumbEls.forEach(el => { el.hidden = true; });

    // Preload images
    const preloadTasks = [];
    for (let i = 0; i < maxMedia; i++) {
      const item = displayItems[i];
      if (item?.type === 'image') {
        const baseUrl = getProductImage(item.handle);
        if (baseUrl) {
          preloadTasks.push(preloadImage(addWidthParam(baseUrl, 1400)));
        }
      }
    }
    await Promise.allSettled(preloadTasks);

    // Update each slot
    for (let i = 0; i < maxMedia; i++) {
      const item = displayItems[i];
      const mediaEl = allMediaEls[i];
      const thumbEl = allThumbEls[i];
      let mediaImg = allMediaImgs[i];
      const thumbImg = allThumbImgs[i];

      if (!item || !mediaEl) continue;

      mediaEl.hidden = false;

      if (item.type === 'image') {
        // Restore original HTML if was a placeholder
        if (mediaEl.hasAttribute('data-bundle-placeholder')) {
          const originalIndex = orig.mediaEls.indexOf(mediaEl);
          if (originalIndex !== -1 && orig.mediaState[originalIndex]?.innerHTML) {
            mediaEl.innerHTML = orig.mediaState[originalIndex].innerHTML;
            mediaImg = $('img', mediaEl);
            allMediaImgs[i] = mediaImg;
          }
        }
        mediaEl.removeAttribute('data-bundle-placeholder');

        const baseUrl = getProductImage(item.handle);
        if (mediaImg && baseUrl) {
          await swapImageSmooth(mediaImg, addWidthParam(baseUrl, 1400));
        }
        if (thumbEl) {
          thumbEl.hidden = false;
          if (thumbImg && baseUrl) {
            await swapImageSmooth(thumbImg, addWidthParam(baseUrl, 300));
          }
        }
      } else if (item.type === 'placeholder') {
        mediaEl.setAttribute('data-bundle-placeholder', 'true');
        const placeholderTemplate = $('template[data-bundle-placeholder-template]');
        if (placeholderTemplate) {
          mediaEl.innerHTML = '';
          const content = placeholderTemplate.content.cloneNode(true);
          const textEl = $('[data-bundle-placeholder-text]', content);
          if (textEl) {
            textEl.textContent = `Add your ${getOrdinal(item.slotIndex)} item`;
          }
          mediaEl.appendChild(content);
        }
        if (thumbEl) thumbEl.hidden = true;
      }
    }
  }

  function setSwatchUI(btn, { selected, soldOut }) {
    const isSelected = !!selected;
    const isSoldOut = !!soldOut;

    // Selection UI
    btn.classList.toggle('is-selected', isSelected);
    btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

    // Clear any theme-level soldout classes that can cause pseudo-element weirdness
    btn.classList.remove('sold_out');
    btn.classList.remove('is-disabled');

    // Our own soldout state (used for styling and logic)
    btn.dataset.bundleSoldout = isSoldOut ? 'true' : 'false';
    btn.classList.toggle('bundle-swatch--soldout', isSoldOut);

    // Actual interactivity
    btn.disabled = isSoldOut;
    btn.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');
    btn.tabIndex = isSoldOut ? -1 : 0;
  }

  function clampSlots(n, min, max) {
    const num = parseInt(n, 10);
    if (!Number.isFinite(num)) return min;
    return Math.max(min, Math.min(max, num));
  }

  function getMaxSlots(root) {
    const max = parseInt(root.dataset.maxSlots || '12', 10);
    return Number.isFinite(max) && max > 0 ? max : 12;
  }

  function getPackSizeFromVariant(variant) {
    // Pack size lives on option1 (Size is option2 in custom mode, option3 in predefined).
    // Try both index 0 and the named property — Shopify variant shapes vary by caller.
    const raw = variant?.options?.[0] || variant?.option1 || variant?.selectedOptions?.[0]?.value || '';
    const n = parsePackSize(raw);
    if (n > 0) return n;
    // Fallback: scan selectedOptions for a value that parses to a pack size.
    const selected = variant?.selectedOptions || variant?.selected_options || [];
    for (const o of selected) {
      const m = parsePackSize(o?.value);
      if (m > 0) return m;
    }
    return 0;
  }

  function getUnitsPerSlot(root) {
    const n = parseInt(root?.dataset?.unitsPerSlot || '1', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  function applyRequiredSlots(root, requiredSlots, { resetSelections = false } = {}) {
    const maxSlots = getMaxSlots(root);
    // Callers pass raw pack size from option1 (e.g. 4 for "4-Pack"); divide by
    // units_per_slot so one color slot represents N base-product units.
    const unitsPerSlot = getUnitsPerSlot(root);
    const slotsForPack = Math.max(1, Math.floor((requiredSlots || 0) / unitsPerSlot));
    const next = clampSlots(slotsForPack, 1, maxSlots);
    const prev = parseInt(root.dataset.requiredSlots || '0', 10) || 0;

    console.log('[bundle-builder] applyRequiredSlots:', { requiredSlots, next, prev, maxSlots, resetSelections });

    root.dataset.requiredSlots = String(next);

    // Don't clear all selections - only clear slots that are being hidden
    // This preserves selections when going from 3-pack to 4-pack, etc.

    getAllSlots(root).forEach(slot => {
      const idx = parseInt(slot.getAttribute('data-bundle-slot') || '0', 10) || 0;
      const shouldHide = idx > next;

      slot.toggleAttribute('hidden', shouldHide);
      slot.classList.toggle('bundle-slot--hidden', shouldHide);

      // Only clear selection for slots being hidden (going down in pack size)
      if (shouldHide) {
        clearSlotSelection(slot);
      }
    });

    updateATCEnabled(root);
    updateBundleGallery(root, null, { skipScroll: true }); // Don't scroll on pack size change
  }

  function getSelections(root) {
    const mode = getMode(root);

    if (mode === 'predefined') {
      // In predefined mode, resolve from the current color option value
      const formId = getFormId(root);
      const colorValue = getSelectedCombo(formId, root);
      const packSize = getPackSize(root);
      return resolveColorValue(colorValue, packSize);
    }

    // Custom mode: get from individual slot selections
    return getActiveSlots(root).map(slot => {
      const sel = $('[data-bundle-swatch].is-selected', slot);
      return sel ? sel.getAttribute('data-handle') : null;
    });
  }

  /**
   * Get the current pack size from the root element or active slots.
   */
  function getPackSize(root) {
    const n = parseInt(root?.dataset?.requiredSlots || '0', 10);
    if (n > 0) return n;
    return getActiveSlots(root).length || parseInt(root?.dataset?.slots || '3', 10);
  }

  /**
   * In predefined mode, programmatically select the hidden slot swatches
   * to match the combo's handles. This keeps getSelections/gallery/ATC consistent.
   */
  function syncSlotsFromCombo(root, handles) {
    const allSlots = getAllSlots(root);
    allSlots.forEach((slot, i) => {
      // Clear existing selection
      clearSlotSelection(slot);

      const targetHandle = handles[i];
      if (!targetHandle) return;

      // Find and select the matching swatch in this hidden slot
      const swatch = $(`[data-bundle-swatch][data-handle="${targetHandle}"]`, slot);
      if (swatch) {
        swatch.classList.add('is-selected');
        swatch.setAttribute('aria-pressed', 'true');
      }
    });
  }

  function updateATCEnabled(root, anySoldOut = false) {
    const atc = $('[data-bundle-atc]');
    if (!atc) return;

    const formId = getFormId(root);
    const size = getSelectedSize(formId, root);
    const mode = getMode(root);

    let ready = false;

    if (mode === 'predefined') {
      const colorValue = getSelectedCombo(formId, root);
      const handles = resolveColorValue(colorValue, getPackSize(root));
      ready = !!size && handles.length > 0 && !anySoldOut;
    } else {
      const picks = getSelections(root);
      ready = !!size && picks.every(Boolean);
    }

    atc.disabled = !ready;
    atc.setAttribute('aria-disabled', ready ? 'false' : 'true');
  }

  /**
   * Refresh sold-out state for all swatches based on selected size.
   * Now uses embedded stock map - no async fetches needed!
   * 
   * In predefined mode, a combo is sold out if ANY of its constituent
   * colors are unavailable in the selected size.
   */
  function refreshSoldOut(root, options = {}) {
    const { skipScroll = false } = options;
    const formId = getFormId(root);
    const mode = getMode(root);
    const size = getSelectedSize(formId, root);

    if (!size) {
      updateATCEnabled(root);
      return;
    }

    if (mode === 'predefined') {
      // Predefined mode: check base product stock for each color in the selection.
      // For "Black/White/Charcoal Grey/Light Blue", all 4 must be available in the selected size.
      const colorValue = getSelectedCombo(formId, root);
      const packSize = getPackSize(root);
      const handles = resolveColorValue(colorValue, packSize);
      const unique = uniqueHandles(handles);

      // Check stock for each unique color
      let anySoldOut = false;
      let soldOutColors = [];
      for (const handle of unique) {
        const stockInfo = getStockInfo(handle, size);
        if (!stockInfo || !stockInfo.available) {
          anySoldOut = true;
          const map = loadStockMap();
          soldOutColors.push(map[handle]?.label || handle);
        }
      }

      // Render the swatch display (mark sold out colors)
      renderPredefinedDisplay(root, handles, colorValue, soldOutColors);

      // Sync hidden slots
      if (handles.length) {
        syncSlotsFromCombo(root, handles);
      }

      // Apply correct sold-out state to theme's variant picker
      applyBundleSoldOut(root, formId);

      updateATCEnabled(root, anySoldOut);
      updateBundleGallery(root, null, { skipScroll });
      return;
    }

    // Custom mode: existing behavior
    const slots = getActiveSlots(root);
    for (const slot of slots) {
      const swatches = $$('[data-bundle-swatch]', slot);

      // Update sold-out states synchronously from embedded map
      for (const btn of swatches) {
        const handle = btn.getAttribute('data-handle');
        const stockInfo = getStockInfo(handle, size);
        const soldOut = !stockInfo || !stockInfo.available;
        setSwatchUI(btn, { selected: btn.classList.contains('is-selected'), soldOut });
      }

      // If the selected swatch becomes unavailable for this size, clear it.
      const current = $('[data-bundle-swatch].is-selected', slot);
      if (current && current.disabled) {
        current.classList.remove('is-selected');
        current.setAttribute('aria-pressed', 'false');
      }

      // Normalize selection UI
      for (const btn of swatches) {
        setSwatchUI(btn, { selected: btn.classList.contains('is-selected'), soldOut: btn.disabled });
      }

      updateSlotLabel(slot);
    }

    updateATCEnabled(root);
    updateBundleGallery(root, null, { skipScroll });
  }

  /**
   * Handle pack size change - called when option1 changes
   */
  function handlePackSizeChange(root, formId) {
    const packSize = getSelectedPackSize(formId);
    console.log('[bundle-builder] Pack size changed to:', packSize);
    
    if (packSize > 0) {
      applyRequiredSlots(root, packSize, { resetSelections: true });
    }
    refreshSoldOut(root, { skipScroll: true });
  }

  function bind(root) {
    // Load the stock map from embedded JSON
    loadStockMap();

    const formId = getFormId(root);
    const mode = getMode(root);
    console.log('[bundle-builder] Initializing with formId:', formId, 'mode:', mode);

    // Force-select the first available size option on bundle pages.
    // Shopify's selected_or_first_available_variant may skip sizes that are
    // "unavailable" on the shell product, even though inventory isn't tracked.
    // Since bundle JS uses the base product's stock map, we pick the first
    // size where enough colors are in stock to fill all pack slots.
    if (formId) {
      const sizePos = mode === 'predefined'
        ? (root.getAttribute('data-shell-size-position') || '3')
        : '2';
      const sizeRadios = Array.from(document.querySelectorAll(
        `input[name="option${sizePos}"][form="${formId}"]`
      ));
      const map = loadStockMap();
      const colorHandles = Object.keys(map);

      // Determine current pack size from option1
      const packSize = (() => {
        const packRadio = document.querySelector(`input[name="option1"][form="${formId}"]:checked`);
        if (packRadio) {
          const n = parseInt(String(packRadio.value).replace(/[^0-9]/g, ''), 10);
          if (n > 0) return n;
        }
        return parseInt(root.dataset.requiredSlots || root.dataset.slots || '2', 10);
      })();

      let bestRadio = null;
      for (const radio of sizeRadios) {
        const sizeVal = radio.value;
        const availableCount = colorHandles.filter(h => {
          const info = getStockInfo(h, sizeVal);
          return info && info.available;
        }).length;
        if (availableCount >= packSize) {
          bestRadio = radio;
          break;
        }
      }

      // Fallback: pick first size with at least 1 color available
      if (!bestRadio) {
        for (const radio of sizeRadios) {
          const sizeVal = radio.value;
          const hasAny = colorHandles.some(h => {
            const info = getStockInfo(h, sizeVal);
            return info && info.available;
          });
          if (hasAny) { bestRadio = radio; break; }
        }
      }

      if (bestRadio && !bestRadio.checked) {
        bestRadio.checked = true;
        bestRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (mode === 'predefined') {
      // ─── PREDEFINED MODE ───────────────────────────────
      // The theme's native variant picker handles color switching.
      // We watch option changes and update our display + gallery + ATC.

      /**
       * Called whenever the color value changes. Resolves handles,
       * renders swatches, syncs slots, updates gallery + ATC.
       */
      function handleColorChange(root, formId) {
        const colorValue = getSelectedCombo(formId, root);
        console.log('[bundle-builder] Predefined color changed:', colorValue);

        const isMobile = window.innerWidth < 1000;
        refreshSoldOut(root, { skipScroll: isMobile });
      }

      if (formId) {
        const sizePos = getSizeOptionPosition(root);
        const colorPos = getColorComboOptionPosition(root);

        document.addEventListener('change', (e) => {
          const t = e.target;
          if (!t) return;

          const isOption1 = t.matches(`input[name="option1"][form="${formId}"]`) ||
                            t.matches(`select[name="option1"][form="${formId}"]`) ||
                            t.matches('input[name="option1"]');

          const isSizeOption = t.matches(`input[name="option${sizePos}"][form="${formId}"]`) ||
                               t.matches(`select[name="option${sizePos}"][form="${formId}"]`) ||
                               t.matches(`input[name="option${sizePos}"]`);

          const isColorOption = t.matches(`input[name="option${colorPos}"][form="${formId}"]`) ||
                                t.matches(`select[name="option${colorPos}"][form="${formId}"]`) ||
                                t.matches(`input[name="option${colorPos}"]`);

          if (isOption1) {
            console.log('[bundle-builder] Pack size (option1) changed:', t.value);
            handlePackSizeChange(root, formId);
            // Re-resolve color for new pack size
            handleColorChange(root, formId);
          }

          if (isSizeOption) {
            console.log('[bundle-builder] Size changed:', t.value);
            refreshSoldOut(root);
          }

          if (isColorOption) {
            console.log('[bundle-builder] Color option changed:', t.value);
            handleColorChange(root, formId);
          }
        });

        // Observe hidden inputs for option1 (pack size) and color option
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
              const target = mutation.target;
              if (target.matches && target.matches('input[name="option1"]')) {
                console.log('[bundle-builder] option1 value attribute changed:', target.value);
                handlePackSizeChange(root, formId);
                handleColorChange(root, formId);
              }
              if (target.matches && target.matches(`input[name="option${colorPos}"]`)) {
                console.log('[bundle-builder] color option value attribute changed:', target.value);
                handleColorChange(root, formId);
              }
            }
          }
        });

        $$('input[name="option1"][type="hidden"]').forEach(input => {
          observer.observe(input, { attributes: true, attributeFilter: ['value'] });
        });
        $$(`input[name="option${colorPos}"][type="hidden"]`).forEach(input => {
          observer.observe(input, { attributes: true, attributeFilter: ['value'] });
        });

        document.addEventListener('variant:change', (e) => {
          const variant = e.detail?.variant;
          if (variant) {
            const packSize = getPackSizeFromVariant(variant);
            if (packSize > 0) {
              applyRequiredSlots(root, packSize, { resetSelections: false });
            }
          }
          handleColorChange(root, formId);
          refreshSoldOut(root);
        });

        // Initial setup
        const initialPackSize = getSelectedPackSize(formId);
        console.log('[bundle-builder] Initial pack size:', initialPackSize);
        if (initialPackSize > 0) {
          applyRequiredSlots(root, initialPackSize);
        }

        // Clear theme's incorrect sold-out state then apply correct state from base product
        function syncVariantPickerState() {
          const section = root.closest('.product-info, .product, section, [data-product]') || document;
          // 1. Strip all theme-applied is-disabled (based on shell product, which has no inventory)
          section.querySelectorAll('.variant-picker .is-disabled').forEach(el => {
            el.classList.remove('is-disabled');
          });
          section.querySelectorAll('.variant-picker .sold_out').forEach(el => {
            el.classList.remove('sold_out');
          });
          section.querySelectorAll('.variant-picker input[type="radio"]:disabled').forEach(input => {
            input.disabled = false;
          });
          // 1b. Un-hide option values the theme hid because the shell product
          //     doesn't have every pack×color×size combination as a real variant.
          //     The base product's stock map is the source of truth, not the shell.
          section.querySelectorAll('.variant-picker [data-option-value][hidden]').forEach(el => {
            el.removeAttribute('hidden');
          });
          // 2. Re-apply correct sold-out state from base product stock
          applyBundleSoldOut(root, formId);
        }

        syncVariantPickerState();

        // Watch for the theme re-adding is-disabled after variant changes
        let syncScheduled = false;
        const variantEnableObserver = new MutationObserver(() => {
          if (syncScheduled) return;
          syncScheduled = true;
          requestAnimationFrame(() => {
            variantEnableObserver.disconnect();
            syncVariantPickerState();
            variantEnableObserver.observe(productSection, {
              subtree: true,
              attributes: true,
              attributeFilter: ['disabled', 'class', 'hidden']
            });
            syncScheduled = false;
          });
        });
        const productSection = root.closest('.product-info, .product, section, [data-product]') || document.body;
        variantEnableObserver.observe(productSection, {
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled', 'class', 'hidden']
        });

        // Initial color render
        handleColorChange(root, formId);
      }

      refreshSoldOut(root, { skipScroll: true });
      updateBundleGallery(root, null, { skipScroll: true });

    } else {
      // ─── CUSTOM MODE (existing behavior) ───────────────

      // Force default state: no preselected colors
      $$('[data-bundle-swatch].is-selected', root).forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      updateAllSlotLabels(root);
      updateATCEnabled(root);

      /* Hybrid shells: prefill each empty slot with the native picker's colour so
         the bundle starts complete. One-time default only — changing the native
         picker afterwards leaves the slots alone. */
      if (root.hasAttribute('data-bundle-hybrid')) setTimeout(() => {
        const nativeInput = document.querySelector('.variant-picker input[name="option1"]:checked');
        const nativeVal = nativeInput && !/^(xs|s|m|l|xl|xxl|2xl|3xl)$/i.test(nativeInput.value) ? nativeInput.value : null;
        if (nativeVal) {
          const h = nativeVal.trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');
          getActiveSlots(root).forEach(slot => {
            if ($('[data-bundle-swatch].is-selected', slot)) return;
            const match = $('[data-bundle-swatch][data-handle="' + h + '"]', slot);
            if (match && !match.disabled) match.click();
          });
        }
      }, 0);

      root.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-bundle-swatch]');
        if (!btn || btn.disabled) return;

        const slot = btn.closest('[data-bundle-slot]');
        if (!slot) return;

        const clickedSlotIndex = parseInt(slot.getAttribute('data-bundle-slot') || '0', 10);

        $$('[data-bundle-swatch]', slot).forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        updateSlotLabel(slot);

        $$('[data-bundle-swatch]', slot).forEach(b => {
          setSwatchUI(b, { selected: b.classList.contains('is-selected'), soldOut: b.disabled });
        });

        updateATCEnabled(root);

        const isMobile = window.innerWidth < 1000;
        updateBundleGallery(root, clickedSlotIndex, { skipScroll: isMobile });
      });

      if (formId) {
        document.addEventListener('change', (e) => {
          const t = e.target;
          if (!t) return;

          const isOption1 = t.matches(`input[name="option1"][form="${formId}"]`) ||
                            t.matches(`select[name="option1"][form="${formId}"]`) ||
                            t.matches(`input[name="option1"]`);

          const isOption2 = t.matches(`input[name="option2"][form="${formId}"]`) ||
                            t.matches(`select[name="option2"][form="${formId}"]`) ||
                            t.matches(`input[name="option2"]`);

          if (isOption1) {
            console.log('[bundle-builder] Pack size (option1) changed:', t.value);
            handlePackSizeChange(root, formId);
          }

          if (isOption2) {
            console.log('[bundle-builder] Size (option2) changed:', t.value);
            refreshSoldOut(root);
            updateAllSlotLabels(root);
          }
        });

        document.addEventListener('variant:change', (e) => {
          console.log('[bundle-builder] variant:change event:', e.detail);

          const variant = e.detail?.variant;
          if (variant) {
            const packSize = getPackSizeFromVariant(variant);
            console.log('[bundle-builder] Pack size from variant:', packSize);
            if (packSize > 0) {
              applyRequiredSlots(root, packSize, { resetSelections: true });
            }
          }
          refreshSoldOut(root);
        });

        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
              const target = mutation.target;
              if (target.matches && target.matches('input[name="option1"]')) {
                console.log('[bundle-builder] option1 (pack size) value attribute changed:', target.value);
                handlePackSizeChange(root, formId);
              }
            }
          }
        });

        $$('input[name="option1"][type="hidden"]').forEach(input => {
          observer.observe(input, { attributes: true, attributeFilter: ['value'] });
        });

        const initialPackSize = getSelectedPackSize(formId);
        console.log('[bundle-builder] Initial pack size:', initialPackSize);
        if (initialPackSize > 0) {
          applyRequiredSlots(root, initialPackSize);
        }

        // Strip theme's sold-out styling from shell product's variant picker.
        // Shell product has no real inventory — our bundle swatches handle stock.
        function syncCustomPickerState() {
          const section = root.closest('.product-info, .product, section, [data-product]') || document;
          // Only strip from option1 (pack) and option2 (size) labels
          ['option1', 'option2'].forEach(optName => {
            section.querySelectorAll(`.variant-picker input[name="${optName}"]`).forEach(input => {
              if (input.disabled) input.disabled = false;
              const label = input.nextElementSibling;
              if (label) {
                label.classList.remove('is-disabled', 'sold_out');
                // Un-hide options the theme hid because the shell product
                // doesn't have every pack×size combination as a real variant.
                if (label.hasAttribute('hidden')) label.removeAttribute('hidden');
              }
            });
          });
          // Also un-hide any [data-option-value] wrappers the theme hid
          section.querySelectorAll('.variant-picker [data-option-value][hidden]').forEach(el => {
            el.removeAttribute('hidden');
          });
        }

        syncCustomPickerState();

        // Watch for theme re-adding is-disabled after variant changes
        let customSyncScheduled = false;
        const customPickerObserver = new MutationObserver(() => {
          if (customSyncScheduled) return;
          customSyncScheduled = true;
          requestAnimationFrame(() => {
            customPickerObserver.disconnect();
            syncCustomPickerState();
            customPickerObserver.observe(customProductSection, {
              subtree: true,
              attributes: true,
              attributeFilter: ['disabled', 'class', 'hidden']
            });
            customSyncScheduled = false;
          });
        });
        const customProductSection = root.closest('.product-info, .product, section, [data-product]') || document.body;
        customPickerObserver.observe(customProductSection, {
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled', 'class', 'hidden']
        });
      }

      refreshSoldOut(root, { skipScroll: true });
      updateAllSlotLabels(root);
      updateBundleGallery(root, null, { skipScroll: true });
    }
  }

  const BOUND_ROOTS = new WeakSet();
  function bindIfNew() {
    const root = getRoot();
    if (!root || BOUND_ROOTS.has(root)) return;
    BOUND_ROOTS.add(root);
    bind(root);
  }
  document.addEventListener('DOMContentLoaded', bindIfNew);
  /* Pack-switcher AJAX replaces the whole product section, discarding the bound
     root, so re-bind afterwards. Driven by the section-load event the swap already
     fires: a body-wide subtree MutationObserver ran a querySelector on every one of
     the thousands of mutations a swap makes, which made switching crawl. */
  document.addEventListener('shopify:section:load', function () { setTimeout(bindIfNew, 0); });
  document.addEventListener('variant:add', function () { setTimeout(bindIfNew, 0); });
  /* Cheap safety net for any path that doesn't emit the event. */
  var pvRebindTicks = 0;
  var pvRebindTimer = setInterval(function () {
    bindIfNew();
    if (++pvRebindTicks > 20) clearInterval(pvRebindTimer);
  }, 500);
})();
